'use client';

import { Button } from 'v5/design-system/Button';
import { cn } from 'v5/platform/dom/cn';

/**
 * Shared “danger” chrome for row-level issues: Zod invalid row and render crashes.
 * Use with {@link EntityListRowRenderErrorPanel} for render failures.
 */
export const entityListRowIssueChromeClassName =
  'rounded-md border border-border-danger bg-bg-danger-muted/30 text-sm';

export type EntityListRowRenderErrorPanelDensity = 'default' | 'compact';

export type EntityListRowRenderErrorPanelProps = {
  title: string;
  message: string;
  tryAgainLabel: string;
  onTryAgain: () => void;
  /** `compact` fits a single table cell (per-cell error boundary). */
  density?: EntityListRowRenderErrorPanelDensity;
  className?: string;
};

/**
 * Presentational block for synchronous render failures (error boundary fallback).
 * “Try again” always stops propagation so row `onClick` is not fired.
 */
export function EntityListRowRenderErrorPanel({
  title,
  message,
  tryAgainLabel,
  onTryAgain,
  density = 'default',
  className
}: EntityListRowRenderErrorPanelProps) {
  const compact = density === 'compact';
  return (
    <div
      className={cn(
        entityListRowIssueChromeClassName,
        compact ? 'p-2' : 'p-3',
        className
      )}
    >
      <p
        className={cn(
          'font-medium text-fg-danger',
          compact ? 'text-xs' : 'text-sm'
        )}
      >
        {title}
      </p>
      <p className="mt-1 break-words text-xs text-fg-muted">{message}</p>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className={cn(compact ? 'mt-1.5' : 'mt-2')}
        onClick={e => {
          e.stopPropagation();
          onTryAgain();
        }}
      >
        {tryAgainLabel}
      </Button>
    </div>
  );
}
